import type { EnrollmentMonthlyConversionResponse } from "@t3tools/contracts";
import type { ChartDataset } from "chart.js";
import { cohortStyle } from "./enrollmentCohortCurves";

export type MonthlyConversionKind = "referral" | "postVisit";

export function monthlyLabel(value: string, long = false): string {
  return new Intl.DateTimeFormat("en-US", {
    month: long ? "long" : "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(value + "T00:00:00Z"));
}

export function monthlyConversionDataset(
  clinic: EnrollmentMonthlyConversionResponse["clinics"][number],
  kind: MonthlyConversionKind,
  index: number,
): ChartDataset<"line", (number | null)[]> {
  const style = cohortStyle(index);
  return {
    label: clinic.name,
    data: clinic.points.map((point) => {
      const result = point[kind];
      return result.numerator === null || result.denominator === 0
        ? null
        : (100 * result.numerator) / result.denominator;
    }),
    borderColor: style.color,
    backgroundColor: style.color,
    borderDash: style.dash,
    borderWidth: 2,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointBackgroundColor: clinic.points.map((point) =>
      point[kind].denominator < 20 ? "#ffffff" : style.color,
    ),
    pointBorderWidth: 2,
    spanGaps: false,
  };
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
function count(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
function date(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(value))
  );
}
function result(value: unknown, measured: boolean): boolean {
  return (
    record(value) &&
    count(value.denominator) &&
    (measured
      ? count(value.numerator) && value.numerator <= value.denominator
      : value.numerator === null)
  );
}
export function isEnrollmentMonthlyConversionResponse(
  value: unknown,
): value is EnrollmentMonthlyConversionResponse {
  return (
    record(value) &&
    value.source === "live" &&
    typeof value.asOf === "string" &&
    Number.isFinite(Date.parse(value.asOf)) &&
    Array.isArray(value.clinics) &&
    value.clinics.every(
      (clinic: unknown) =>
        record(clinic) &&
        typeof clinic.id === "string" &&
        typeof clinic.name === "string" &&
        count(clinic.undatedReferrals) &&
        Array.isArray(clinic.points) &&
        clinic.points.length === 9 &&
        clinic.points.every(
          (point: unknown) =>
            record(point) &&
            date(point.month) &&
            point.month.endsWith("-01") &&
            date(point.measurementDate) &&
            typeof point.measured === "boolean" &&
            result(point.referral, point.measured) &&
            result(point.postVisit, point.measured) &&
            record(point.postVisit) &&
            count(point.postVisit.excludedPrior),
        ),
    )
  );
}
